import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { obtenerListaPagos, obtenerDetallePago, obtenerXmlPago, enviarCorreoPago } from "../services/pagosService";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import Toast, { useToast } from "../components/Toast";

const currentYear = new Date().getFullYear();
const yearsOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const fmtCurrency = (n) =>
  (Number(n) || 0).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (s) => {
  if (!s) return "";
  const clean = String(s).replace(/\//g, "-").slice(0, 10);
  try {
    const [y, m, d] = clean.split("-");
    return `${d}/${m}/${y}`;
  } catch {
    return s;
  }
};

const TIPO_LABELS = {
  TRF: "Transferencia",
  CH: "Cheque",
  EFE: "Efectivo",
  TC: "T. Credito",
  TD: "T. Debito",
  "99": "Otro",
};

export default function PagosListaPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const empresaId = Number(localStorage.getItem("empresaId") || 1);

  const currentMonth = new Date().getMonth() + 1;
  const [filters, setFilters] = useState({
    anio: String(currentYear),
    mes: String(currentMonth),
    clienteSearch: "",
  });

  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal detalle
  const [detalle, setDetalle] = useState(null); // { folio, rows: [] }
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState(null);

  // Email modal
  const [emailModal, setEmailModal] = useState(null); // { folio, nombreCliente, moneda, importeTotal }
  const [emailForm, setEmailForm] = useState({ destinatario: "", asunto: "", cuerpo: "" });
  const [sendingEmail, setSendingEmail] = useState(false);

  const cargar = useCallback(async (anio, mes) => {
    setLoading(true);
    try {
      const data = await obtenerListaPagos(empresaId, Number(anio), Number(mes));
      setPagos(data || []);
    } catch {
      toast.error("Error al cargar la lista de pagos.");
      setPagos([]);
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => {
    cargar(filters.anio, filters.mes);
  }, [filters.anio, filters.mes, cargar]);

  const onChangeFilter = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  };

  const filtered = useMemo(() => {
    const term = filters.clienteSearch.trim().toLowerCase();
    if (!term) return pagos;
    return pagos.filter(
      (p) =>
        (p.cliente || "").toLowerCase().includes(term) ||
        (p.nombreCliente || "").toLowerCase().includes(term)
    );
  }, [pagos, filters.clienteSearch]);

  const totalImporte = useMemo(
    () => filtered.reduce((s, p) => s + (Number(p.importeTotal) || 0), 0),
    [filtered]
  );

  const openDetalle = async (folio) => {
    setDetalle({ folio, rows: [] });
    setDetalleLoading(true);
    try {
      const rows = await obtenerDetallePago(folio, empresaId);
      setDetalle({ folio, rows: rows || [] });
    } catch {
      toast.error("No se pudo cargar el detalle del pago.");
      setDetalle(null);
    } finally {
      setDetalleLoading(false);
    }
  };

  const openEmailModal = (r) => {
    const fmtMonto = fmtCurrency(r.importeTotal);
    const monedaStr = (r.moneda === "USD" || r.moneda === "D") ? "USD" : "MXN";
    setEmailForm({
      destinatario: r.emailCxP || "",
      asunto: `Complemento de Pago CP-${r.folio} – LMD Integradora`,
      cuerpo: `Estimado(a) ${r.nombreCliente || r.cliente},\n\nAdjuntamos el complemento de pago CP-${r.folio} por un importe de $${fmtMonto} ${monedaStr}.\n\nQuedamos a sus órdenes para cualquier aclaración.\n\nAtentamente,\nCrédito y Cobranza LMD Integradora\ncredito@lmdint.mx`,
    });
    setEmailModal(r);
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailForm.destinatario.trim()) return;
    setSendingEmail(true);
    try {
      await enviarCorreoPago({
        folio: emailModal.folio,
        empresa: empresaId,
        destinatario: emailForm.destinatario.trim(),
        asunto: emailForm.asunto,
        cuerpo: emailForm.cuerpo,
      });
      toast.success(`Correo enviado a ${emailForm.destinatario}`);
      setEmailModal(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Error al enviar el correo.";
      toast.error(msg);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDownloadXml = async (folio) => {
    setDownloadingKey(folio);
    try {
      const xml = await obtenerXmlPago(folio, empresaId);
      const blob = new Blob([xml], { type: "application/xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CP_${folio}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(`XML no encontrado para CP-${folio}.`);
    } finally {
      setDownloadingKey(null);
    }
  };

  const columns = [
    {
      key: "folio",
      title: "Folio",
      style: { width: 110 },
      render: (r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <button
            onClick={() => openDetalle(r.folio)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "var(--clr-primary)", fontWeight: "var(--fw-semibold)",
              fontSize: "var(--fs-sm)", padding: 0,
              fontFamily: "var(--font-mono)",
            }}
            title="Ver documentos aplicados"
          >
            CP-{r.folio}
          </button>
          {r.tieneCFDI ? (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 99,
              background: "#dcfce7", color: "#15803d", border: "1px solid #bbf7d0",
              letterSpacing: 0.3,
            }}>
              ✓ CFDI
            </span>
          ) : (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 99,
              background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0",
              letterSpacing: 0.3,
            }}>
              Sin CFDI
            </span>
          )}
        </div>
      ),
    },
    { key: "cliente", title: "Cliente", style: { width: 100 } },
    { key: "nombreCliente", title: "Nombre" },
    {
      key: "fecha",
      title: "Fecha",
      style: { width: 110 },
      render: (r) => fmtDate(r.fecha),
    },
    {
      key: "tipoDocPago",
      title: "Tipo Pago",
      style: { width: 120 },
      render: (r) => TIPO_LABELS[r.tipoDocPago] ?? r.tipoDocPago,
    },
    { key: "numDocPago", title: "Ref / Num Doc", style: { width: 130 } },
    {
      key: "importeTotal",
      title: "Importe",
      style: { width: 140 },
      className: "num",
      render: (r) => fmtCurrency(r.importeTotal),
    },
    {
      key: "moneda",
      title: "Moneda",
      style: { width: 80 },
      className: "center",
      render: (r) => (
        <span className={`lmd-badge ${r.moneda === "USD" ? "lmd-badge-primary" : "lmd-badge-neutral"}`}>
          {r.moneda === "USD" || r.moneda === "D" ? "USD" : "MXN"}
        </span>
      ),
    },
    {
      key: "acciones",
      title: "",
      style: { width: 80 },
      render: (r) =>
        r.tieneCFDI ? (
          <div style={{ display: "flex", gap: 2 }}>
            <button
              onClick={() => navigate(`/pagos/cp/print/${r.folio}/${empresaId}`)}
              title="Imprimir complemento de pago"
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: "var(--clr-primary)", fontSize: 14, padding: "2px 4px",
              }}
            >
              🖨️
            </button>
            <button
              onClick={() => handleDownloadXml(r.folio)}
              disabled={downloadingKey === r.folio}
              title="Descargar XML del complemento"
              style={{
                background: "none", border: "none",
                cursor: downloadingKey === r.folio ? "wait" : "pointer",
                color: downloadingKey === r.folio ? "var(--clr-text-muted)" : "var(--clr-primary)",
                fontSize: 14, padding: "2px 4px",
              }}
            >
              {downloadingKey === r.folio ? "⏳" : "⬇️"}
            </button>
            <button
              onClick={() => openEmailModal(r)}
              title="Enviar complemento por correo"
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: "var(--clr-primary)", fontSize: 14, padding: "2px 4px",
              }}
            >
              ✉️
            </button>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <Toast toasts={toast.toasts} onClose={toast.closeToast} />

      <PageHeader
        title="Lista de Pagos"
        subtitle="Historial de pagos aplicados por mes"
      />

      {/* Filtros */}
      <div className="lmd-card" style={{ marginBottom: "var(--sp-4)" }}>
        <div className="lmd-card-header">
          <h3 className="lmd-card-title">Filtros</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <span style={{ fontSize: "var(--fs-sm)", color: "var(--clr-text-muted)" }}>
              {filtered.length} pagos
            </span>
            <span style={{ fontWeight: "var(--fw-bold)", color: "var(--clr-primary)" }}>
              Total: ${fmtCurrency(totalImporte)}
            </span>
          </div>
        </div>
        <div className="lmd-card-body">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "var(--sp-4)" }}>
            <div className="lmd-field">
              <label className="lmd-label">Año</label>
              <select className="lmd-select" name="anio" value={filters.anio} onChange={onChangeFilter} style={{ width: "100%" }}>
                {yearsOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div className="lmd-field">
              <label className="lmd-label">Mes</label>
              <select className="lmd-select" name="mes" value={filters.mes} onChange={onChangeFilter} style={{ width: "100%" }}>
                {MESES.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div className="lmd-field">
              <label className="lmd-label">Cliente</label>
              <input
                className="lmd-input"
                name="clienteSearch"
                value={filters.clienteSearch}
                onChange={onChangeFilter}
                placeholder="Clave o nombre..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="lmd-card">
        {loading && (
          <div className="lmd-loading-bar">
            <div className="lmd-spinner" />
            Cargando pagos...
          </div>
        )}
        {!loading && (
          <DataTable columns={columns} rows={filtered} keyField="folio" />
        )}
      </div>

      {/* ── MODAL CORREO ── */}
      {emailModal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setEmailModal(null); }}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 1100,
            display: "flex", justifyContent: "center", alignItems: "center",
          }}
        >
          <div style={{
            background: "#fff",
            width: "90%", maxWidth: 560,
            borderRadius: 8, padding: 28,
            position: "relative",
            boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          }}>
            <button
              onClick={() => setEmailModal(null)}
              style={{ position: "absolute", top: 12, right: 16, border: "none", background: "none", fontSize: 20, cursor: "pointer", color: "var(--clr-text-secondary)" }}
            >
              ✕
            </button>
            <h3 style={{ margin: "0 0 4px", fontSize: "var(--fs-lg)", fontWeight: "var(--fw-bold)" }}>
              Enviar complemento por correo
            </h3>
            <p style={{ margin: "0 0 20px", fontSize: "var(--fs-sm)", color: "var(--clr-text-muted)" }}>
              CP-{emailModal.folio} — {emailModal.nombreCliente || emailModal.cliente}
            </p>
            <form onSubmit={handleSendEmail} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div className="lmd-field">
                <label className="lmd-label">Para (correo del cliente) *</label>
                <input
                  className="lmd-input"
                  type="text"
                  value={emailForm.destinatario}
                  onChange={(e) => setEmailForm((f) => ({ ...f, destinatario: e.target.value }))}
                  placeholder="cxp@cliente.com;otro@cliente.com"
                  required
                />
              </div>
              <div className="lmd-field">
                <label className="lmd-label">Asunto</label>
                <input
                  className="lmd-input"
                  type="text"
                  value={emailForm.asunto}
                  onChange={(e) => setEmailForm((f) => ({ ...f, asunto: e.target.value }))}
                />
              </div>
              <div className="lmd-field">
                <label className="lmd-label">Mensaje</label>
                <textarea
                  className="lmd-input"
                  rows={7}
                  value={emailForm.cuerpo}
                  onChange={(e) => setEmailForm((f) => ({ ...f, cuerpo: e.target.value }))}
                  style={{ resize: "vertical", fontFamily: "inherit" }}
                />
              </div>
              <p style={{ fontSize: "var(--fs-xs)", color: "var(--clr-text-muted)", margin: 0 }}>
                Se enviará desde <strong>credito@lmdint.mx</strong> con el XML adjunto.
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" className="lmd-btn lmd-btn-secondary" onClick={() => setEmailModal(null)}>
                  Cancelar
                </button>
                <button type="submit" className="lmd-btn lmd-btn-primary" disabled={sendingEmail}>
                  {sendingEmail ? "Enviando..." : "✉ Enviar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL DETALLE ── */}
      {detalle && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setDetalle(null); }}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 1000,
            display: "flex", justifyContent: "center", alignItems: "center",
          }}
        >
          <div style={{
            background: "#fff",
            width: "90%", maxWidth: 640,
            borderRadius: 8, padding: 24,
            position: "relative",
            maxHeight: "80vh", overflowY: "auto",
            boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          }}>
            <button
              onClick={() => setDetalle(null)}
              style={{ position: "absolute", top: 12, right: 16, border: "none", background: "none", fontSize: 20, cursor: "pointer", color: "var(--clr-text-secondary)" }}
            >
              ✕
            </button>

            <h3 style={{ margin: "0 0 4px", fontSize: "var(--fs-lg)", fontWeight: "var(--fw-bold)" }}>
              Pago CP-{detalle.folio}
            </h3>
            <p style={{ margin: "0 0 16px", fontSize: "var(--fs-sm)", color: "var(--clr-text-muted)" }}>
              Documentos aplicados en este pago
            </p>

            {detalleLoading ? (
              <div className="lmd-loading-bar"><div className="lmd-spinner" /> Cargando...</div>
            ) : detalle.rows.length === 0 ? (
              <p style={{ color: "var(--clr-text-muted)", fontSize: "var(--fs-sm)" }}>
                No se encontraron registros para este pago.
              </p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--clr-bg)", borderBottom: "2px solid var(--clr-border)" }}>
                    {["Tipo", "Serie", "Factura", "Importe", "Moneda"].map((h, i) => (
                      <th key={i} style={{
                        padding: "8px 12px",
                        fontSize: "var(--fs-xs)", fontWeight: "var(--fw-semibold)",
                        color: "var(--clr-text-secondary)", textTransform: "uppercase",
                        textAlign: i >= 3 ? "right" : "left",
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detalle.rows.map((row, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc", borderBottom: "1px solid var(--clr-border-subtle)" }}>
                      <td style={{ padding: "8px 12px" }}>{row.tipoDoc}</td>
                      <td style={{ padding: "8px 12px" }}>{row.serie}</td>
                      <td style={{ padding: "8px 12px", fontFamily: "var(--font-mono)" }}>
                        <button
                          onClick={() => window.open(`/facturacion/detalle/${row.serie}/${row.factura}/${empresaId}`, "_blank")}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--clr-primary)", fontWeight: "var(--fw-semibold)", fontFamily: "var(--font-mono)", padding: 0, textDecoration: "underline" }}
                          title="Ver factura"
                        >
                          {row.factura}
                        </button>
                      </td>
                      <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "var(--font-mono)" }}>
                        {fmtCurrency(row.importe)}
                      </td>
                      <td style={{ padding: "8px 12px", textAlign: "right" }}>
                        <span className={`lmd-badge ${row.moneda === "D" ? "lmd-badge-primary" : "lmd-badge-neutral"}`}>
                          {row.moneda === "D" ? "USD" : "MXN"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: "2px solid var(--clr-border)", background: "var(--clr-bg)" }}>
                    <td colSpan={3} style={{ padding: "8px 12px", fontWeight: "var(--fw-semibold)", textAlign: "right", fontSize: "var(--fs-sm)", color: "var(--clr-text-secondary)" }}>
                      Total:
                    </td>
                    <td style={{ padding: "8px 12px", textAlign: "right", fontFamily: "var(--font-mono)", fontWeight: "var(--fw-extrabold)", color: "var(--clr-primary)" }}>
                      {fmtCurrency(detalle.rows.reduce((s, r) => s + Number(r.importe), 0))}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      )}
    </>
  );
}
